import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../navbar/Navbar";
import { getAllModels, getBuildStatus } from "../../services/aiModels";
import "./Models.css";
import "../shared/starfield.css";

const ModelList = ({ entity }) => {
    // entity: 'model' | 'tool' | undefined (both, with a tab toggle)
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedModelForDetail, setSelectedModelForDetail] = useState(null);
    const [entityTab, setEntityTab] = useState(entity || 'model');

    // React-Router keeps this component mounted when navigating between
    // /Models and /Tools (same component, different `entity` prop). Without
    // this sync, the internal tab stays stuck on whatever entity it was
    // initially mounted with.
    useEffect(() => {
        if (entity) setEntityTab(entity);
    }, [entity]);
    const showTabs = !entity;
    const navigate = useNavigate();

    const fetchModels = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getAllModels();
            setModels(data);
        } catch (err) {
            console.error("Error fetching models:", err);
            toast.error("Failed to fetch models.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchModels();
    }, [fetchModels]);

    // Light background refetch so models pushed via the CLI (or from another
    // tab) show up without the user needing to reload. Kept infrequent so it
    // doesn't hammer the API. Build-status polling below stays finer-grained.
    useEffect(() => {
        const listInterval = setInterval(() => {
            fetchModels();
        }, 15000);
        return () => clearInterval(listInterval);
    }, [fetchModels]);

    // Poll build status for models that are importing or building
    useEffect(() => {
        const inProgressModelIds = models
            .filter(m => m.build_status === 'building' || m.build_status === 'importing' || m.build_status === 'pending')
            .map(m => m.id);

        if (inProgressModelIds.length === 0) return;

        const pollInterval = setInterval(async () => {
            for (const modelId of inProgressModelIds) {
                try {
                    const status = await getBuildStatus(modelId);
                    const currentModel = models.find(m => m.id === modelId);
                    const prevStatus = currentModel?.build_status;

                    // Check if status changed
                    if (status.build_status !== prevStatus) {
                        // Refresh the list when status changes
                        fetchModels();
                        if (status.build_status === 'ready') {
                            toast.success(`Model "${status.name || 'Model'}" is ready!`);
                        } else if (status.build_status === 'failed') {
                            toast.error(`Build failed: ${status.build_error || 'Unknown error'}`);
                        } else if (prevStatus === 'importing' && status.build_status === 'building') {
                            toast.info(`Building Docker image for "${status.name || 'Model'}"...`);
                        }
                    }
                } catch (err) {
                    console.error("Error polling build status:", err);
                }
            }
        }, 3000); // Poll every 3 seconds for better responsiveness

        return () => clearInterval(pollInterval);
    }, [models, fetchModels]);

    const handleRowClick = (model) => {
        // If model has a source repository, navigate to the repository detail page
        if (model.repository) {
            navigate(`/Registry/${model.repository.full_name}`);
        } else {
            setSelectedModelForDetail(model);
        }
    };

    const getBuildStatusBadge = (model) => {
        const statusConfig = {
            importing: { icon: 'fa-spinner fa-spin', label: 'Importing', class: 'importing' },
            pending: { icon: 'fa-clock', label: 'Pending', class: 'pending' },
            building: { icon: 'fa-spinner fa-spin', label: 'Building', class: 'building' },
            ready: { icon: 'fa-check', label: 'Ready', class: 'ready' },
            failed: { icon: 'fa-times', label: 'Failed', class: 'failed' },
        };
        const config = statusConfig[model.build_status] || statusConfig.pending;
        return (
            <span className={`build-status-badge ${config.class}`} title={model.build_error || ''}>
                <i className={`fa ${config.icon}`}></i>
                {config.label}
            </span>
        );
    };

    return (
        <div className="models-page">
            {/* Starfield Background */}
            <div className="starfield-background"></div>

            <Navbar />

            <div className="container py-4">
                {/* Header */}
                <div className="models-header-card">
                    <div className="models-header-content">
                        <div className="models-info">
                            <h1 className="models-title">
                                {entity === 'tool' ? 'Tools' : entity === 'model' ? 'Models' : 'Models & Tools'}
                            </h1>
                            <p className="models-description">
                                {entity === 'tool'
                                    ? 'Manage your processing tools'
                                    : entity === 'model'
                                        ? 'Manage your AI models'
                                        : 'Manage your AI models and processing tools'}
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={fetchModels}
                                disabled={loading}
                                title="Refetch from server (useful after a CLI push)"
                            >
                                <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''} me-2`}></i>
                                Refresh
                            </button>
                            {entity !== 'tool' && (
                                <button
                                    className="btn-create-model"
                                    onClick={() => navigate('/Models/New')}
                                >
                                    <i className="fa fa-plus me-2"></i>
                                    New Model
                                </button>
                            )}
                            {entity !== 'model' && (
                                <button
                                    className="btn-create-model"
                                    onClick={() => navigate('/Models/NewTool')}
                                    style={{ background: 'linear-gradient(135deg, #6c757d, #495057)' }}
                                >
                                    <i className="fa fa-plus me-2"></i>
                                    New Tool
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Stats Row */}
                <div className="models-stats">
                    <div className="stat-card">
                        <div className="stat-icon">
                            <i className="fa fa-cube"></i>
                        </div>
                        <div className="stat-value">{models.length}</div>
                        <div className="stat-label">Total Models</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">
                            <i className="fa fa-check-circle"></i>
                        </div>
                        <div className="stat-value">
                            {models.filter(m => m.build_status === 'ready').length}
                        </div>
                        <div className="stat-label">Ready</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">
                            <i className="fa fa-spinner"></i>
                        </div>
                        <div className="stat-value">
                            {models.filter(m => m.build_status === 'importing' || m.build_status === 'building' || m.build_status === 'pending').length}
                        </div>
                        <div className="stat-label">In Progress</div>
                    </div>
                </div>

                {/* Entity Type Tabs (only when showing both) */}
                {showTabs && (
                    <div className="registry-type-tabs mb-3">
                        <button
                            className={`registry-type-tab ${entityTab === 'model' ? 'active' : ''}`}
                            onClick={() => setEntityTab('model')}
                        >
                            <i className="fa fa-brain me-2"></i>
                            AI Models
                            <span className="ms-2 badge bg-secondary">{models.filter(m => !m.entity_type || m.entity_type === 'model').length}</span>
                        </button>
                        <button
                            className={`registry-type-tab ${entityTab === 'tool' ? 'active' : ''}`}
                            onClick={() => setEntityTab('tool')}
                        >
                            <i className="fa fa-wrench me-2"></i>
                            Tools
                            <span className="ms-2 badge bg-secondary">{models.filter(m => m.entity_type === 'tool').length}</span>
                        </button>
                    </div>
                )}

                {/* Models/Tools Table */}
                <div className="models-card">
                    <div className="models-card-header">
                        <div className="models-card-title">
                            <i className={`fa ${entityTab === 'tool' ? 'fa-wrench' : 'fa-brain'} models-card-icon`}></i>
                            <div>
                                <h3>{entityTab === 'tool' ? 'All Tools' : 'All Models'}</h3>
                                <p className="models-card-subtitle">
                                    {entityTab === 'tool' ? 'View and manage your processing tools' : 'View and manage your AI models'}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="models-card-content">
                        {(() => {
                            const filteredModels = models.filter(m =>
                                entityTab === 'tool'
                                    ? m.entity_type === 'tool'
                                    : (!m.entity_type || m.entity_type === 'model')
                            );
                            if (loading) return (
                            <div className="loading-state">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        );
                        if (filteredModels.length === 0) return (
                            <div className="empty-state">
                                <i className={`fa ${entityTab === 'tool' ? 'fa-wrench' : 'fa-brain'}`}></i>
                                <p>{entityTab === 'tool' ? 'No tools yet' : 'No AI models yet'}</p>
                                <small className="text-muted">
                                    {entityTab === 'tool'
                                        ? 'Create your first tool to get started with data processing'
                                        : 'Create your first model to get started with AI-powered analysis'}
                                </small>
                                <button
                                    className="btn-create-model mt-3"
                                    onClick={() => navigate(entityTab === 'tool' ? '/Models/NewTool' : '/Models/New')}
                                    style={entityTab === 'tool' ? { background: 'linear-gradient(135deg, #6c757d, #495057)' } : {}}
                                >
                                    <i className="fa fa-plus me-2"></i>
                                    {entityTab === 'tool' ? 'Create Tool' : 'Create Model'}
                                </button>
                            </div>
                        );
                        return (
                            <table className="models-table">
                                <thead>
                                    <tr>
                                        <th>{entityTab === 'tool' ? 'Tool' : 'Model'}</th>
                                        <th>Type</th>
                                        <th>Build Status</th>
                                        <th>Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredModels.map((model) => (
                                        <tr
                                            key={model.id}
                                            className="model-row clickable-row"
                                            onClick={() => handleRowClick(model)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td>
                                                <div className="model-name-cell">
                                                    <i
                                                        className={`fa ${model.icon || 'fa-cube'}`}
                                                        style={{ color: model.color || '#6f42c1', marginRight: '0.5rem' }}
                                                    ></i>
                                                    <div>
                                                        <strong>{model.name}</strong>
                                                        {model.is_owner ? (
                                                            <span
                                                                className={`badge ms-2 ${model.is_public ? 'bg-success' : 'bg-secondary'}`}
                                                                title={model.is_public ? 'Public' : 'Private'}
                                                            >
                                                                <i className={`fa ${model.is_public ? 'fa-globe' : 'fa-lock'} me-1`}></i>
                                                                {model.is_public ? 'Public' : 'Private'}
                                                            </span>
                                                        ) : (
                                                            <span className="badge ms-2 bg-info" title="Shared with you">
                                                                <i className="fa fa-share-alt me-1"></i>
                                                                Shared
                                                            </span>
                                                        )}
                                                        {!model.is_owner && model.created_by_user && (
                                                            <small className="d-block text-muted">
                                                                by {model.created_by_user.first_name || model.created_by_user.username}
                                                            </small>
                                                        )}
                                                        {model.description && model.is_owner && (
                                                            <small className="d-block text-muted">
                                                                {model.description.substring(0, 50)}
                                                                {model.description.length > 50 ? '...' : ''}
                                                            </small>
                                                        )}
                                                        {/* Show warning if model is no longer available */}
                                                        {!model.is_owner && model.is_added && !model.is_available && (
                                                            <small className="d-block text-danger">
                                                                <i className="fa fa-exclamation-triangle me-1"></i>
                                                                Model no longer available (made private by owner)
                                                            </small>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{model.model_type_display || model.model_type || "-"}</td>
                                            <td>{getBuildStatusBadge(model)}</td>
                                            <td>{new Date(model.created_at).toLocaleDateString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        );
                        })()}
                    </div>
                </div>
            </div>

            {/* Model Detail Modal */}
            {selectedModelForDetail && (
                <div
                    className="modal-overlay"
                    onClick={() => setSelectedModelForDetail(null)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1050
                    }}
                >
                    <div
                        className="model-detail-modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            padding: '24px',
                            maxWidth: '600px',
                            width: '90%',
                            maxHeight: '80vh',
                            overflow: 'auto',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                        }}
                    >
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                            <i
                                className={`fa ${selectedModelForDetail.icon || 'fa-cube'}`}
                                style={{
                                    color: selectedModelForDetail.color || '#6f42c1',
                                    fontSize: '32px',
                                    marginRight: '16px'
                                }}
                            ></i>
                            <div style={{ flex: 1 }}>
                                <h4 style={{ margin: 0 }}>{selectedModelForDetail.name}</h4>
                                <small className="text-muted">
                                    {selectedModelForDetail.model_type_display || selectedModelForDetail.model_type}
                                </small>
                            </div>
                            <button
                                onClick={() => setSelectedModelForDetail(null)}
                                style={{
                                    border: 'none',
                                    background: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#6c757d'
                                }}
                            >
                                <i className="fa fa-times"></i>
                            </button>
                        </div>

                        {/* Description */}
                        {selectedModelForDetail.description && (
                            <div style={{ marginBottom: '20px' }}>
                                <h6 style={{ color: '#6c757d', marginBottom: '8px' }}>Description</h6>
                                <p style={{ margin: 0 }}>{selectedModelForDetail.description}</p>
                            </div>
                        )}

                        {/* Build Status */}
                        <div style={{ marginBottom: '20px' }}>
                            <h6 style={{ color: '#6c757d', marginBottom: '8px' }}>Build Status</h6>
                            {getBuildStatusBadge(selectedModelForDetail)}
                            {selectedModelForDetail.build_error && (
                                <p className="text-danger mt-2" style={{ fontSize: '0.85rem' }}>
                                    {selectedModelForDetail.build_error}
                                </p>
                            )}
                        </div>

                        {/* Repository Info */}
                        {selectedModelForDetail.repository ? (
                            <div style={{
                                background: '#f8f9fa',
                                borderRadius: '8px',
                                padding: '16px',
                                marginBottom: '20px'
                            }}>
                                <h6 style={{ color: '#6c757d', marginBottom: '12px' }}>
                                    <i className="fa fa-code-branch me-2"></i>
                                    Source Repository
                                </h6>
                                <div style={{ marginBottom: '12px' }}>
                                    <strong>Repository:</strong>{' '}
                                    <code style={{
                                        background: '#e9ecef',
                                        padding: '2px 8px',
                                        borderRadius: '4px'
                                    }}>
                                        {selectedModelForDetail.repository.full_name}
                                    </code>
                                </div>
                                <div style={{ marginBottom: '12px' }}>
                                    <strong>Clone URL:</strong>
                                    <div style={{
                                        background: '#1e1e1e',
                                        color: '#d4d4d4',
                                        padding: '12px',
                                        borderRadius: '6px',
                                        marginTop: '4px',
                                        fontFamily: 'monospace',
                                        fontSize: '0.85rem',
                                        wordBreak: 'break-all'
                                    }}>
                                        git clone {window.location.origin}{selectedModelForDetail.repository.clone_url}
                                    </div>
                                </div>
                                {selectedModelForDetail.repository.last_commit_sha && (
                                    <div style={{ marginBottom: '8px' }}>
                                        <strong>Last Commit:</strong>{' '}
                                        <code style={{
                                            background: '#e9ecef',
                                            padding: '2px 8px',
                                            borderRadius: '4px'
                                        }}>
                                            {selectedModelForDetail.repository.last_commit_sha.substring(0, 7)}
                                        </code>
                                        {selectedModelForDetail.repository.last_commit_message && (
                                            <span className="text-muted ms-2">
                                                - {selectedModelForDetail.repository.last_commit_message.substring(0, 50)}
                                                {selectedModelForDetail.repository.last_commit_message.length > 50 ? '...' : ''}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{
                                background: '#f8f9fa',
                                borderRadius: '8px',
                                padding: '16px',
                                marginBottom: '20px',
                                textAlign: 'center',
                                color: '#6c757d'
                            }}>
                                <i className="fa fa-upload" style={{ fontSize: '24px', marginBottom: '8px', display: 'block' }}></i>
                                <p style={{ margin: 0 }}>
                                    This model was uploaded directly, not from a git repository.
                                </p>
                            </div>
                        )}

                        {/* Parameters */}
                        {selectedModelForDetail.parameters_schema &&
                         Object.keys(selectedModelForDetail.parameters_schema).length > 0 && (
                            <div style={{ marginBottom: '20px' }}>
                                <h6 style={{ color: '#6c757d', marginBottom: '8px' }}>
                                    <i className="fa fa-sliders me-2"></i>
                                    Parameters ({Object.keys(selectedModelForDetail.parameters_schema).length})
                                </h6>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                    gap: '8px'
                                }}>
                                    {Object.entries(selectedModelForDetail.parameters_schema).map(([key, config]) => (
                                        <div
                                            key={key}
                                            style={{
                                                background: '#e9ecef',
                                                padding: '8px 12px',
                                                borderRadius: '6px',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            <strong>{config.label || key}</strong>
                                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                {config.type}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Close Button */}
                        <div style={{ textAlign: 'right' }}>
                            <button
                                className="btn btn-secondary"
                                onClick={() => setSelectedModelForDetail(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModelList;
